import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Autocomplete,
  Button,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Tooltip,
  Card,
  CardContent,
  InputAdornment,
  CircularProgress,
  Stack,
} from "@mui/material";
import { RemoveCircleOutline, Search } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import useFetchObjects from "../../../../api/useFetchObjects";
import useAdd from "../../../../api/useAdd";
import { useAuth } from "../../../../context";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// Debounce function
function debounce(func, delay) {
  let timeout;
  return function (...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), delay);
  };
}

const PatientTreatment = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);
  const [searchTerm, setSearchTerm] = useState("");
  const [allPatients, setAllPatients] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [patientInputValue, setPatientInputValue] = useState("");
  const [forceUpdate, setForceUpdate] = useState(0);

  const { data: procedures } = useFetchObjects(
    ["dental-procedure"],
    "dental-procedure",
    token
  );

  const { data: dentists, isLoading: loadingDentists } = useFetchObjects(
    ["users"],
    "users/?position=7",
    token
  );

  const {
    data: patientsData,
    isLoading: isLoadingPatients,
    refetch,
  } = useFetchObjects(
    ["patients", page, pageSize, searchTerm, forceUpdate],
    `patients/?page=${page}&page_size=${pageSize}&search=${searchTerm}`,
    token
  );

  const [treatmentData, setTreatmentData] = useState({
    treatment_date: new Date().toISOString().split("T")[0],
    dentist: auth?.user?.user?.id || "",
    patient: "",
    is_paid: false,
    total_cost: 0,
    discount: 0,
    details: [],
  });

  const [formErrors, setFormErrors] = useState({});
  const [images, setImages] = useState([]);
  const [imageErrors, setImageErrors] = useState([]);

  const [selectedDetails, setSelectedDetails] = useState([]);
  const [autocompleteInput, setAutocompleteInput] = useState("");
  const autocompleteRef = useRef(null);

  const { handleAdd, loading, resetForm } = useAdd("dental-treatments", token);

  useEffect(() => {
    if (patientsData?.results) {
      setAllPatients((prevPatients) => {
        const newPatients = [...prevPatients, ...patientsData.results];
        // Remove duplicates based on patient ID
        const uniquePatients = newPatients.reduce((acc, current) => {
          if (!acc.find((item) => item.id === current.id)) {
            acc.push(current);
          }
          return acc;
        }, []);
        return uniquePatients;
      });
      setHasMore(patientsData.next !== null);
    } else {
      setHasMore(false);
    }
  }, [patientsData]);

  useEffect(() => {
    // Fetch initial data when component mounts
    if (!searchTerm) {
      setAllPatients([]);
      setPage(1);
      setHasMore(true);
      refetch();
    }
  }, []);

  useEffect(() => {
    if (searchTerm !== "") {
      setAllPatients([]);
      setPage(1);
      setHasMore(true);
      refetch();
    } else {
      // When searchTerm is cleared, fetch initial data
      setAllPatients([]);
      setPage(1);
      setHasMore(true);
      refetch();
    }
  }, [searchTerm, refetch]);

  useEffect(() => {
    if (resetForm) {
      navigate("/dental/patient-treatments-list");
    }
  }, [resetForm]);

  // Patient Autocomplete Handlers
  const handlePatientInputChange = (event, newInputValue) => {
    setPatientInputValue(newInputValue);
    debouncedSetSearchTerm(newInputValue);
  };

  const handlePatientChange = (event, newValue) => {
    setTreatmentData({
      ...treatmentData,
      patient: newValue ? newValue.id : "",
    });
  };

  const handleScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.target;
    if (
      scrollHeight - scrollTop <= clientHeight + 50 &&
      hasMore &&
      !isLoadingPatients
    ) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  // Debounced setSearchTerm
  const debouncedSetSearchTerm = useCallback(
    debounce((value) => {
      setSearchTerm(value);
    }, 300),
    []
  );

  // Function to force update
  const forceUpdateAutocomplete = () => {
    setForceUpdate((prev) => prev + 1);
  };

  useEffect(() => {
    if (searchTerm === "") {
      forceUpdateAutocomplete();
    }
  }, [searchTerm]);

  // Recalculate total_price for each detail and overall total cost
  const calculateTotalCost = () => {
    const updatedDetails = selectedDetails.map((detail) => {
      const toothNum = parseFloat(detail.tooth_number);
      const cost = parseFloat(detail.cost);

      // Multiply tooth_number * cost, if tooth_number invalid treat as 1
      const multiplier = !isNaN(toothNum) && toothNum > 0 ? toothNum : 1;
      const baseCost = !isNaN(cost) ? cost : 0;
      const total_price = multiplier * baseCost;

      return {
        ...detail,
        total_price,
      };
    });

    setSelectedDetails(updatedDetails);

    const subtotal = updatedDetails.reduce(
      (sum, detail) => sum + detail.total_price,
      0
    );

    const effectiveDiscount = Math.min(treatmentData.discount, subtotal);
    const total = subtotal - effectiveDiscount;

    setTreatmentData((prevData) => ({
      ...prevData,
      total_cost: total.toFixed(2),
    }));
  };

  const handleAddProcedure = (procedure) => {
    if (procedure) {
      setSelectedDetails((prevDetails) => [
        ...prevDetails,
        {
          procedureName: procedure.name,
          procedure,
          cost: procedure.cost,
          tooth_number: "",
          total_price: 0,
        },
      ]);
      setAutocompleteInput("");
    }
  };

  const handleRemoveDetail = (index) => {
    const updatedDetails = [...selectedDetails];
    updatedDetails.splice(index, 1);
    setSelectedDetails(updatedDetails);
  };

  const handleUpdateDetail = (index, field, value) => {
    const updatedDetails = [...selectedDetails];
    updatedDetails[index][field] = value;

    // Recalculate total_price for this detail immediately
    const toothNum = parseFloat(
      field === "tooth_number" ? value : updatedDetails[index].tooth_number
    );
    const cost = parseFloat(
      field === "cost" ? value : updatedDetails[index].cost
    );

    const multiplier = !isNaN(toothNum) && toothNum > 0 ? toothNum : 1;
    const baseCost = !isNaN(cost) ? cost : 0;
    updatedDetails[index].total_price = multiplier * baseCost;

    setSelectedDetails(updatedDetails);
  };

  const handleImageChange = (event, index) => {
    const file = event.target.files[0];
    if (file) {
      const updatedImages = [...images];
      updatedImages[index] = file;
      setImages(updatedImages);
    }
  };

  const addImageField = () => {
    setImages([...images, null]);
  };

  const removeImageField = (index) => {
    const updatedImages = [...images];
    updatedImages.splice(index, 1);
    setImages(updatedImages);
  };

  useEffect(() => {
    calculateTotalCost();
  }, [selectedDetails, treatmentData.discount]);

  const handleChangeField = (e) => {
    const { name, value } = e.target;
    setTreatmentData({ ...treatmentData, [name]: value });
  };

  const validateImages = () => {
    let isValid = true;
    const errors = [];
    images.forEach((image, index) => {
      if (!image) {
        errors[index] = t("call_center.imageRequired");
        isValid = false;
      } else if (image.size > 2 * 1024 * 1024) {
        errors[index] = t("call_center.imageTooLarge");
        isValid = false;
      } else if (!image.type.startsWith("image/")) {
        errors[index] = t("call_center.invalidImageType");
        isValid = false;
      }
    });
    setImageErrors(errors);
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!treatmentData.patient) {
      setFormErrors({ patient: t("dental.patientRequired") });
      return;
    }

    if (!treatmentData.dentist) {
      setFormErrors({ dentist: t("dental.dentistRequired") });
      return;
    }

    if (!validateImages()) {
      return;
    }

    const formData = new FormData();

    formData.append("treatment_date", treatmentData.treatment_date);
    formData.append("dentist", treatmentData.dentist);
    formData.append("patient", treatmentData.patient);
    formData.append("is_paid", treatmentData.is_paid);
    formData.append("total_cost", treatmentData.total_cost);
    formData.append("discount", treatmentData.discount);

    // Include updated details with total_price field
    formData.append(
      "details",
      JSON.stringify(
        selectedDetails.map((detail) => ({
          procedure: detail.procedure.id,
          tooth_number: detail.tooth_number,
          cost: detail.cost,
          total_price: detail.total_price,
        }))
      )
    );

    images.forEach((image) => {
      if (image) {
        formData.append(`images`, image);
      }
    });

    handleAdd(formData);
  };

  return (
    <Card
      sx={{
        boxShadow: "0px 20px 50px rgba(0, 0, 0, 0.1)",
        borderRadius: 4,
        backgroundColor: "#f4f6f8",
        width: "100%",
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Typography
          variant="h5"
          fontWeight={600}
          color="primary"
          textAlign="center"
          gutterBottom
        >
          {t("dental.addTreatmentTitle")}
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Box mb={4}>
          <Typography variant="subtitle1" fontWeight={500} mb={2}>
            {t("dental.patientInfo")}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("dental.treatmentDate")}
                name="treatment_date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={treatmentData.treatment_date}
                onChange={handleChangeField}
                sx={{ backgroundColor: "#fff", borderRadius: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={allPatients}
                getOptionLabel={(option) =>
                  `${option.firstname} ${option.lastname || ""} (${
                    option.phone || "N/A"
                  })`.trim()
                }
                inputValue={patientInputValue}
                onInputChange={handlePatientInputChange}
                onChange={handlePatientChange}
                ListboxProps={{
                  onScroll: handleScroll,
                }}
                loading={isLoadingPatients}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("dental.patient")}
                    placeholder={t("dental.searchByIdOrName")}
                    error={!!formErrors.patient}
                    helperText={formErrors.patient}
                    sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <React.Fragment>
                          {isLoadingPatients ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              {loadingDentists ? (
                <CircularProgress size={24} />
              ) : (
                <Autocomplete
                  options={dentists || []}
                  getOptionLabel={(option) =>
                    `${option.first_name} ${option.last_name}`
                  }
                  value={
                    dentists.find(
                      (dentist) => dentist.id === treatmentData.dentist
                    ) || null
                  }
                  onChange={(event, value) =>
                    setTreatmentData({
                      ...treatmentData,
                      dentist: value ? value.id : "",
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("dental.dentist")}
                      error={!!formErrors.dentist}
                      helperText={formErrors.dentist}
                      sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                    />
                  )}
                />
              )}
            </Grid>
          </Grid>
        </Box>

        {/* Rest of the component remains the same */}
        <Divider sx={{ my: 3 }} />

        <Box mb={4}>
          <Typography variant="subtitle1" fontWeight={500} mb={2}>
            {t("dental.addProcedure")}
          </Typography>
          <Autocomplete
            ref={autocompleteRef}
            value={null}
            inputValue={autocompleteInput}
            onInputChange={(event, newInputValue) =>
              setAutocompleteInput(newInputValue)
            }
            onChange={(event, value) => handleAddProcedure(value)}
            options={procedures || []}
            getOptionLabel={(option) => option.name}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("dental.searchProcedure")}
                placeholder={t("dental.searchProcedurePlaceholder")}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ backgroundColor: "#fff", borderRadius: 2 }}
              />
            )}
          />
        </Box>

        <TableContainer component={Paper} elevation={1} sx={{ mb: 4 }}>
          <Table>
            <TableHead sx={{ backgroundColor: "#eef2f5" }}>
              <TableRow>
                <TableCell>{t("dental.procedureName")}</TableCell>
                <TableCell align="center">{t("dental.toothNumber")}</TableCell>
                <TableCell align="center">
                  {t("dental.procedureCost")}
                </TableCell>
                <TableCell align="center">
                  {t("dental.totalPrice", "Total Price")}
                </TableCell>
                <TableCell align="center">{t("dental.action")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ bgcolor: "#ccfcfc" }}>
              {selectedDetails.map((detail, index) => (
                <TableRow key={index} hover>
                  <TableCell>{detail.procedureName}</TableCell>
                  <TableCell align="center">
                    <TextField
                      size="small"
                      type="number"
                      placeholder={t("dental.enterToothNumber")}
                      value={detail.tooth_number}
                      onChange={(e) =>
                        handleUpdateDetail(
                          index,
                          "tooth_number",
                          e.target.value
                        )
                      }
                      sx={{ width: "100px" }}
                    />
                  </TableCell>
                  <TableCell align="center">{detail.cost || "N/A"}</TableCell>
                  <TableCell align="center">
                    {detail.total_price?.toFixed(2) || "0.00"}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={t("dental.removeProcedure")} arrow>
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveDetail(index)}
                      >
                        <RemoveCircleOutline />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Images Section */}
        <Grid item xs={12}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              backgroundColor: "#417690",
              color: "white",
              padding: "10px",
              borderRadius: "5px",
            }}
          >
            {t("call_center.uploadImages")}
          </Typography>
          {images.map((image, index) => (
            <React.Fragment key={index}>
              <Box
                sx={{
                  height: "40px",
                  width: "100%",
                  backgroundColor: "#F8F8F8",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  variant="body2"
                  color="text.primary"
                  paddingLeft={1}
                >
                  {t("call_center.image")} #{index + 1}
                </Typography>
                <IconButton
                  onClick={() => removeImageField(index)}
                  sx={{ color: "red" }}
                >
                  <HighlightOffIcon />
                </IconButton>
              </Box>
              <Box
                sx={{
                  minHeight: "70px",
                  width: "100%",
                  backgroundColor: "white",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <Grid container marginTop={1} marginBottom={1}>
                  <Grid item xl={3} lg={3} md={3} sm={3} xs={4}>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      paddingLeft={1}
                      sx={{
                        fontSize: {
                          xl: "15px",
                          lg: "15px",
                          md: "14px",
                          sm: "12px",
                          xs: "10px",
                        },
                      }}
                    >
                      {t("call_center.imageFile")}:
                    </Typography>
                  </Grid>
                  <Grid item xl={9} lg={9} md={9} sm={9} xs={8}>
                    <Box
                      sx={{
                        border: "none",
                        outline: "none",
                        cursor: "pointer",
                        backgroundColor: "transparent",
                        "&::-webkit-file-upload-button": {
                          cursor: "pointer",
                          borderRadius: "4px",
                          padding: "5px 10px",
                          marginRight: 1,
                          border: "none",
                        },
                      }}
                      component={"input"}
                      type="file"
                      onChange={(e) => handleImageChange(e, index)}
                      accept="image/*"
                    />
                    <Typography fontSize="10px" color="error">
                      {imageErrors[index]}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </React.Fragment>
          ))}
          <Box
            sx={{
              height: "40px",
              width: "100%",
              backgroundColor: "#F8F8F8",
              color: "black",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <Button
              onClick={addImageField}
              startIcon={<AddIcon sx={{ color: "green" }} />}
              size="small"
              sx={{
                color: "#477893",
                fontSize: "12px",
                textTransform: "none",
              }}
            >
              {t("call_center.addImage")}
            </Button>
          </Box>
        </Grid>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 4,
          }}
        >
          <Box>
            <TextField
              fullWidth
              label={t("dental.discount")}
              name="discount"
              type="number"
              InputProps={{
                endAdornment: <InputAdornment position="end">؋</InputAdornment>,
              }}
              value={treatmentData.discount}
              onChange={handleChangeField}
              sx={{ backgroundColor: "#fff", borderRadius: 2, width: "200px" }}
            />
          </Box>
          <Typography
            variant="h6"
            color="primary"
            sx={{
              fontWeight: { xs: 400, md: 600 },
              fontSize: { xs: "12px", md: "16px" },
            }}
          >
            {t("dental.totalCost")}: {treatmentData.total_cost} ؋
          </Typography>
          <Button
            size="medium"
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            sx={{
              fontWeight: { xs: 400, md: 700 },
              backgroundColor: "primary.main",
              "&:hover": {
                backgroundColor: "primary.dark",
              },
            }}
          >
            {loading ? t("dental.saving") : t("dental.saveTreatment")}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PatientTreatment;
